import React from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { Heading } from '@atlassian/learning-components';
import { useLoaderData } from 'react-router-dom';

import { Taglist } from 'src/components/TagList';
import { LearningPathLoaderProps } from 'src/pages/OldLearningPathPage';

interface NewCourseHeadingProps {}

export const LearningPathHeading: React.FC<NewCourseHeadingProps> = () => {
  const { learningPath } = useLoaderData() as LearningPathLoaderProps;

  const hasProducts = learningPath.product?.length > 0;
  const hasRoles = learningPath.productRole?.length > 0;

  return (
    <div css={headerContainerStyles} data-testid="new-learning-path-heading">
      <div css={headingAndSubheadingStyles} data-testid="heading-and-subheading">
        <Heading level={1} color={token('color.text')} data-testid="learning-path-title">
          {learningPath.title}
        </Heading>
        {learningPath?.learningPathTagline && (
          <p css={subHeadingStyles} data-testid="learning-path-tagline">
            {learningPath.learningPathTagline}
          </p>
        )}
      </div>
      <div css={metadataContainerStyles} data-testid="metadata-container">
        {hasProducts && (
          <div css={metadataGroupStyles} data-testid="products-group">
            <div css={metadataLabelStyles} data-testid="products-label">
              PRODUCTS
            </div>
            <div css={metadataItemsStyles} data-testid="products-items">
              {Taglist(learningPath.product)}
            </div>
          </div>
        )}
        {hasRoles && (
          <div css={metadataGroupStyles} data-testid="role-group">
            <div css={metadataLabelStyles} data-testid="role-label">
              ROLE
            </div>
            <div css={metadataItemsStyles} data-testid="role-items">
              <span css={metadataValueStyles} data-testid="role-value">
                {learningPath.productRole.join(', ')}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const headerContainerStyles = css({
  display: 'flex',
  maxWidth: '813px',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: token('space.300'),
  paddingBottom: '18px',

  '@media (max-width: 1279px)': {
    maxWidth: '513px',
  },

  '@media (max-width: 920px)': {
    paddingBottom: token('space.300'),
    width: '100%',
    maxWidth: 'unset',
  },
});

const headingAndSubheadingStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: token('space.100'),
});

const metadataContainerStyles = css({
  display: 'flex',
  alignItems: 'baseline',
  gap: token('space.300'),

  '@media (max-width: 920px)': {
    flexDirection: 'column',
    gap: token('space.100'),
    borderTop: '1px solid #DFE1E6',
    borderBottom: '1px solid #DFE1E6',
    padding: `${token('space.200')}  0`,
    width: '100%',
  },
});

const metadataGroupStyles = css({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-start',
  gap: token('space.050'),

  '@media (max-width: 920px)': {
    display: 'flex',
    gap: token('space.200'),
    alignSelf: 'stretch',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    width: '100%',
    alignItems: 'baseline',
  },
});

const metadataItemsStyles = css({
  display: 'flex',
  flexWrap: 'wrap',
  width: '100%',
  maxWidth: 'calc(813px / 2)',

  '@media (max-width: 1279px)': {
    maxWidth: 'calc(513px / 2)',
  },

  '@media (max-width: 920px)': {
    maxWidth: 'none',
    flex: 1,
    gap: token('space.050'),
  },
});

const metadataLabelStyles = css({
  color: '#172B4D',
  fontFamily: 'Charlie Display',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: '600',
  lineHeight: '24px',

  '@media (max-width: 920px)': {
    fontSize: '14px',
    lineHeight: '20px',
  },
});

const subHeadingStyles = css({
  color: '#172B4D',
  fontFamily: 'Inter',
  fontSize: '20px',
  fontStyle: 'normal',
  fontWeight: '400',
  lineHeight: '32px',
});

const metadataValueStyles = css({
  color: '#172B4D',
  fontFamily: 'Inter',
  fontSize: '20px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '32px',

  '@media (max-width: 920px)': {
    fontSize: '14px',
    lineHeight: '20px',
  },
});
